import { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GOLD, GOLD2, GOLDF } from '../theme/theme';
import {
  IMG_S1_LEFT, IMG_S1_RIGHT, IMG_S2_LEFT, IMG_S2_RIGHT,
  IMG_S3_LEFT, IMG_S3_RIGHT, IMG_S4_LEFT, IMG_S4_RIGHT,
} from '../assets/topSlideshowImages';

const SLIDES = [
  {
    left:  { img: IMG_S1_LEFT,  title: 'Old Courtallam Falls', sub: "Nature's Timeless Beauty" },
    right: { img: IMG_S1_RIGHT, title: 'A Grand Beginning',    sub: 'Your Dream Lifestyle Awaits' },
  },
  {
    left:  { img: IMG_S2_LEFT,  title: 'The Falls, Up Close',  sub: 'A Local Favourite, Year After Year' },
    right: { img: IMG_S2_RIGHT, title: 'Premium Lifestyle',    sub: 'Resort-Style Living in the Hills' },
  },
  {
    left:  { img: IMG_S3_LEFT,  title: 'Scenic Courtallam Views', sub: 'Lush Western Ghats, Right Outside' },
    right: { img: IMG_S3_RIGHT, title: 'Prime Connectivity',      sub: 'Well Connected to Falls, Temples & Town' },
  },
  {
    left:  { img: IMG_S4_LEFT,  title: 'The Healing Climate', sub: 'Fresh Mountain Air, Every Morning' },
    right: { img: IMG_S4_RIGHT, title: 'Lush Western Ghats',  sub: 'Surrounded by Living Green' },
  },
];

function Half({ data, roundedSide }) {
  return (
    <Box sx={{
      position: 'relative',
      flex: 1,
      overflow: 'hidden',
      borderTopRightRadius: roundedSide === 'right' ? '16px' : 0,
    }}>
      <Box
        key={data.img}
        component="img"
        src={data.img}
        alt={data.title}
        sx={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          animation: 'topSlideFade 0.8s ease forwards',
          '@keyframes topSlideFade': {
            from: { opacity: 0 },
            to:   { opacity: 1 },
          },
        }}
      />
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: '48%',
        background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.30) 65%, transparent 100%)',
      }} />
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 1.4, md: 2 } }}
        key={`label-${data.title}`}
      >
        <Typography sx={{
          fontFamily: "'Cinzel', Georgia, serif",
          fontWeight: 800,
          fontSize: { xs: '13px', md: '18px' },
          color: '#fff',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
          animation: 'topLabelIn 0.6s ease forwards',
          '@keyframes topLabelIn': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          {data.title}
        </Typography>
        <Typography sx={{
          fontSize: { xs: '10px', md: '13px' },
          fontStyle: 'italic',
          color: GOLDF,
          mt: 0.3,
        }}>
          {data.sub}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Live crossfading top region of the poster — the only part that actually
 * animates. Everything else (brochure panel, feature cards, layout map)
 * is the original static poster artwork. Matches the poster's visual
 * language (rounded top-right corner, gold divider, caption style) so it
 * reads as one seamless piece, with working arrow + dot navigation.
 */
export default function TopSlideshow({ current, onPrev, onNext }) {
  const slide = SLIDES[current];

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      aspectRatio: { xs: '1036 / 480', md: '1036 / 336' },
      display: 'flex',
      borderTopRightRadius: '16px',
      overflow: 'hidden',
    }}>
      <Half data={slide.left}  roundedSide="left" />
      <Box sx={{ width: '3px', background: `linear-gradient(${GOLD}, ${GOLD2})`, flexShrink: 0 }} />
      <Half data={slide.right} roundedSide="right" />

      <IconButton onClick={onPrev} size="small" sx={{
        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
        color: GOLDF, border: `1.5px solid ${GOLDF}`,
        background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)',
        width: { xs: 26, md: 34 }, height: { xs: 26, md: 34 },
        '&:hover': { background: 'rgba(0,0,0,0.55)' },
      }}>
        <ChevronLeftIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
      </IconButton>
      <IconButton onClick={onNext} size="small" sx={{
        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
        color: GOLDF, border: `1.5px solid ${GOLDF}`,
        background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)',
        width: { xs: 26, md: 34 }, height: { xs: 26, md: 34 },
        '&:hover': { background: 'rgba(0,0,0,0.55)' },
      }}>
        <ChevronRightIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
      </IconButton>
    </Box>
  );
}

export { SLIDES };
